/**
 * Shared marked instance.
 *
 * One `Marked` instance is configured at module load with the same security
 * rules (escape raw HTML, open links in a new tab) that the message-stream
 * surface needs, so every caller — TextBlock, MarkdownRenderer, support
 * MessageBubble — produces identical HTML for the same input.
 *
 * Using `new Marked()` (instance-based) instead of the module-global `marked`
 * means each call is isolated; consumers can't accidentally clobber each
 * other's renderer config via `marked.use(...)`.
 */

import { Marked } from "marked";
import { MEMORY_ROOT, PAGE_SEED_FOLDERS, TREE } from "@bash/shared";
import { isBrainRecallEnabled } from "./brain-recall-flag";

const instance = new Marked({ gfm: true, breaks: false });
const SAFE_LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:"]);
const SAFE_IMAGE_PROTOCOLS = new Set(["http:", "https:"]);

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isSafeUrl(href: string, allowedProtocols: Set<string>): boolean {
  const trimmed = href.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("#") || trimmed.startsWith("/")) return true;

  try {
    return allowedProtocols.has(new URL(trimmed).protocol);
  } catch {
    return false;
  }
}

// A brain page target: knowledge-relative, one to three folders, kebab slugs
// (the page grammar without its `knowledge/` prefix and its `.md` extension).
const BRAIN_TARGET_RE = /^(?:[a-z0-9][a-z0-9-]*\/){1,3}[a-z0-9][a-z0-9-]*$/;
const WIKILINK_RE = /\[\[([^\]|\n]{1,200})(?:\|([^\]\n]{1,200}))?\]\]/g;

/** The tiers as a tree-relative path spells them, composed from the contract the
 *  same way the Brain room composes them (`components/brain/brainTree.ts`,
 *  `brainEvidence.ts`) — never spelled here. */
const KNOWLEDGE_TIER = TREE.knowledge.root.slice(MEMORY_ROOT.length + 1);
const EVIDENCE_TIER = TREE.evidence.root.slice(MEMORY_ROOT.length + 1);
const EVIDENCE_DOORS = new Set<string>(TREE.evidence.doors);
const MARKDOWN = ".md";

/** A proof card below its tier: `<door>/<id>`, and `<door>/<app>/<id>` for the
 *  one door that files by app (`evidenceStubPath`). The door is checked against
 *  the contract's list, so a folder naming no door is no card. */
const EVIDENCE_TAIL_RE =
  /^([a-z0-9][a-z0-9-]*)\/((?:[a-z0-9][a-z0-9-]*\/)?[a-z0-9][a-z0-9-]*)$/;

/**
 * WHERE A PATH THAT NAMES NO TIER IS ALLOWED TO START (Ruling P4T2-R22).
 *
 * `folder/slug` is the shape of a brain page and equally the shape of a repo
 * file, and an answer puts both in backticks — `docs/runbooks/worktrees.md` and
 * `apps/web/src/lib` reach the walker as top-level code spans exactly like
 * `people/shubham-rao` does. So a path that never says where it lives is read as
 * a page only when it starts in a folder the brain is seeded with; anything else
 * stays code. Every segment of every seed counts, which is what admits both the
 * `entities/` a nested seed sits under and the `people/` the pages are filed in.
 *
 * SEEDS, NOT CAGES still holds: a folder the reconciler invents is one
 * `knowledge/` away from being cited, and the prefixed forms keep the wide
 * grammar. This narrows one ambiguous spelling, not the tree.
 */
const BARE_ROOTS = new Set<string>([
  ...PAGE_SEED_FOLDERS.flatMap((folder) => folder.split("/")),
  TREE.knowledge.learnings.slice(TREE.knowledge.root.length + 1),
]);

const TAG_RE = /<[^>]*>/g;
/** Regions a citation may not open inside. `code`/`pre` are shown verbatim, and
 *  an anchor's label cannot hold a second anchor. */
const OPENS_CLOSED_REGION = /^<(code|pre|a)\b/i;
const CLOSES_CLOSED_REGION = /^<\/(code|pre|a)\s*>/i;
/** A code SPAN and the whole of its content. Marked wraps a fenced block in
 *  `<pre>` and puts the language on its `<code>`, so a bare `<code>` holding no
 *  tag of its own is the inline form and nothing else. Sticky: it is only ever
 *  tried at the `<code>` the walker is standing on. */
const CODE_SPAN_RE = /<code>([^<>]*)<\/code>/y;

/** The anchor both citation forms produce. NOTHING HERE IS ESCAPED — see the
 *  note in `citation`; the callers' alphabets are `[a-z0-9/.-]` and a label that
 *  came from the author arrived as marked's own escape. */
function chip(path: string, text: string): string {
  const href = `/knowledge/brain?path=${encodeURIComponent(path)}`;
  return `<a class="brain-cite" href="${href}" title="from your brain">${text}</a>`;
}

/** The slug read as words: the last segment, dashes spaced. */
function humanSlug(target: string): string {
  return (target.split("/").pop() ?? target).replace(/-/g, " ").trim();
}

/**
 * A BRAIN PATH IS A CITATION TOO, whatever form it arrives in.
 *
 * The affordance tells the model to cite only by wikilink, and the staging drill
 * showed it naming pages by path in backticks anyway when asked where something
 * came from. A reader should not have to know which spelling they got, so a code
 * span whose WHOLE content is a brain path becomes the same chip.
 *
 * Accepted: a page, with or without the `memory/` root and the `.md` extension,
 * written from the knowledge tier or from the folder below it; and a proof card
 * under the evidence tier, which keeps its own path rather than being read as a
 * page. Anything else is code and stays code — including a `folder/slug` that
 * names neither the root nor the tier and does not start in a seed (`BARE_ROOTS`).
 */
function pathCitation(raw: string): string | undefined {
  const rooted = raw.startsWith(`${MEMORY_ROOT}/`)
    ? raw.slice(MEMORY_ROOT.length + 1)
    : raw;
  const bare = rooted.endsWith(MARKDOWN)
    ? rooted.slice(0, -MARKDOWN.length)
    : rooted;

  if (bare.startsWith(`${EVIDENCE_TIER}/`)) {
    const tail = EVIDENCE_TAIL_RE.exec(bare.slice(EVIDENCE_TIER.length + 1));
    if (!tail || !EVIDENCE_DOORS.has(tail[1])) return undefined;
    // A card is labelled by its cite id exactly as written: an id is a handle,
    // not words, so spacing its dashes would only blur what it names.
    return chip(`${bare}${MARKDOWN}`, tail[2].split("/").pop() ?? tail[2]);
  }

  // Did the path say where it lives? `memory/` names the tree root and
  // `knowledge/` names the tier; either one is the author pointing at the brain,
  // and both keep the wide grammar.
  const tiered = bare.startsWith(`${KNOWLEDGE_TIER}/`);
  const namesItsPlace = rooted !== raw || tiered;
  const target = tiered ? bare.slice(KNOWLEDGE_TIER.length + 1) : bare;
  if (!BRAIN_TARGET_RE.test(target)) return undefined;
  if (!namesItsPlace && !BARE_ROOTS.has(target.split("/")[0])) return undefined;
  return chip(`${KNOWLEDGE_TIER}/${target}${MARKDOWN}`, humanSlug(target));
}

function citation(whole: string, target: string, label?: string): string {
  const t = target.trim();
  if (!BRAIN_TARGET_RE.test(t)) return whole;
  // NOTHING HERE IS ESCAPED, because nothing here is raw. This runs on marked's
  // OUTPUT: an explicit label already arrived as marked's own escape, so a
  // second pass would show the reader `&amp;#39;` where the apostrophe was. And
  // the slug fallback is composed from the target, whose alphabet the regex
  // above pins to `[a-z0-9/-]` — no character in it needs escaping, in the text
  // or in the href.
  //
  // A hyphen only becomes a space in the FALLBACK. Inside an explicit label it
  // is the author's own punctuation ("Q4 Go-Live"), never a slug separator.
  return chip(
    `${KNOWLEDGE_TIER}/${t}${MARKDOWN}`,
    label?.trim() || humanSlug(t)
  );
}

/**
 * Both citation forms, on the rendered html: `[[folder/slug]]` /
 * `[[folder/slug|Label]]` in the prose (P4 T2 §4), and a code span that holds
 * nothing but a brain page path (the P4 T2 follow-up).
 *
 * It runs on the RENDERED html, which is why it walks tags instead of calling
 * `.replace` on the whole string. Marked escapes `<`, `>` and `&` but NOT
 * brackets, so `[[a/b]]` survives verbatim into three places it must never be
 * rewritten: inside an attribute (a bare url with a wikilink in its query
 * string autolinks to `href="…?q=[[a/b]]"`, and opening an anchor there emits
 * attacker-shaped attribute names into the tag), inside `<code>`/`<pre>` (code
 * is shown as written), and inside an existing `<a>` label (nested anchors).
 * Only the text between tags, outside those regions, is a citation.
 *
 * The code span is the one place `<code>` is not the end of it: a span at the
 * TOP LEVEL is a candidate, and the very same depth count is what keeps a fence
 * (`<pre><code>`) and a link label off the list. A span that is not a path is
 * left exactly as marked wrote it.
 */
export function linkBrainCitations(html: string): string {
  let out = "";
  let cursor = 0;
  let closed = 0;
  TAG_RE.lastIndex = 0;
  for (let tag = TAG_RE.exec(html); tag; tag = TAG_RE.exec(html)) {
    const text = html.slice(cursor, tag.index);
    out += closed > 0 ? text : text.replace(WIKILINK_RE, citation);
    cursor = tag.index + tag[0].length;

    // `closed` is still the depth OUTSIDE this tag, so this fires only on a
    // span nothing encloses: never inside a fence's `<pre>`, never inside an
    // anchor's label, never inside another `<code>`.
    if (closed === 0 && tag[0] === "<code>") {
      CODE_SPAN_RE.lastIndex = tag.index;
      const span = CODE_SPAN_RE.exec(html);
      const cite = span ? pathCitation(span[1]) : undefined;
      if (span && cite) {
        out += cite;
        cursor = CODE_SPAN_RE.lastIndex;
        TAG_RE.lastIndex = cursor;
        continue;
      }
    }

    out += tag[0];
    if (OPENS_CLOSED_REGION.test(tag[0])) closed++;
    else if (CLOSES_CLOSED_REGION.test(tag[0]) && closed > 0) closed--;
  }
  const tail = html.slice(cursor);
  return out + (closed > 0 ? tail : tail.replace(WIKILINK_RE, citation));
}

instance.use({
  renderer: {
    link({ href, text }: { href: string; text: string }) {
      if (!href || !isSafeUrl(href, SAFE_LINK_PROTOCOLS)) {
        return text;
      }
      return `<a href="${escapeHtml(
        href
      )}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    },
    image({
      href,
      title,
      text,
    }: {
      href: string;
      title?: string | null;
      text: string;
    }) {
      // A CITATION IS NOT A URL, AND ITS CAPTION IS STILL THE READER'S WORDS.
      // The Brain writes evidence as `![caption](ev:<door>:<id>)`, which
      // `components/wiki/wikiMarkdown` recognises and renders properly — but its
      // scan is bounded, and a reference past the bound arrives here. Dropping
      // it whole took the caption with the picture, which is failing OPEN on the
      // one surface that exists to be checkable (Ruling 238). The words are the
      // floor; nothing else about an unsafe source changes.
      if (href?.startsWith("ev:")) {
        return escapeHtml(text);
      }
      if (!href || !isSafeUrl(href, SAFE_IMAGE_PROTOCOLS)) {
        return "";
      }
      const titleAttr = title ? ` title="${escapeHtml(title)}"` : "";
      return `<img src="${escapeHtml(href)}" alt="${escapeHtml(
        text
      )}"${titleAttr}>`;
    },
    // Model output may include raw HTML (web-page tool results, etc.).
    // Escape it so the visible output never contains arbitrary markup,
    // mirroring ReactMarkdown's default behavior.
    html({ text }: { text: string }) {
      return escapeHtml(text);
    },
  },
  hooks: {
    postprocess(html: string) {
      // Wrap tables in an overflow container so wide code blocks inside
      // cells don't break the page layout and cause horizontal scrolling.
      const wrapped = html
        .replace(/(<table[^>]*>)/gi, '<div class="overflow-x-auto w-full">$1')
        .replace(/(<\/table>)/gi, "$1</div>");
      return isBrainRecallEnabled() ? linkBrainCitations(wrapped) : wrapped;
    },
  },
});

export function renderMarkdown(text: string): string {
  return instance.parse(text) as string;
}

/**
 * A markdown file, by declared mime or extension — the one predicate the
 * creations glimpse and the in-app viewer share, so "what counts as md"
 * can never drift between the preview and the click-through.
 */
export function isMarkdownFile(
  filename?: string | null,
  mimeType?: string | null
): boolean {
  if (mimeType === "text/markdown") return true;
  return /\.(md|mdx|markdown)$/i.test(filename ?? "");
}
